/**
 * The shop-level list screens: jobs, customers, catalog.
 *
 * Search is on every one of them — the thing Estimator Bench had no way to do,
 * and the first thing anyone wants when a customer rings about a job from eight
 * months ago.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '../app/SessionProvider.js';
import { STATUS_LABEL } from '../fixtures/shop.js';
import { currentStage, shotsTotalFor, type Stage } from '../app/jobStage.js';
import type { CatalogItem, CustomerSummary, JobSummary } from '../data/repo.js';
import type { CatalogEntry } from '../estimate/build.js';
import type { RateProfile } from '../calc/types.js';
import {
  BODY_STYLES, Combo, MAKES, PART_NAMES, PART_TYPE_OPTIONS,
} from '../components/Combo.js';
import {
  parseCatalogCsv, SAMPLE_CSV, toCatalogCsv, type ParseResult,
} from '../estimate/catalogCsv.js';

const STAGE_ORDER: Stage[] = ['capture', 'review', 'price', 'issue'];
const STAGE_LABEL: Record<Stage, string> = {
  capture: 'Capture', review: 'Review', price: 'Price', issue: 'Issue',
};

/**
 * Where an OPEN job sits on its own four-step track. Nothing past `'price'`
 * ever shows here — see `currentStage`'s own doc comment on why a list can't
 * know pricing is actually finished without loading each job's estimate.
 */
function StageTrack({ job }: { job: JobSummary }) {
  const shotsTotal = shotsTotalFor(job.impact);
  const stage = currentStage({
    shotsTaken: job.shotsTaken, shotsTotal,
    proposalsTotal: job.proposalsTotal, proposalsDecided: job.proposalsDecided,
  });
  const at = STAGE_ORDER.indexOf(stage);
  const shotsLeft = Math.max(0, shotsTotal - job.shotsTaken);
  // 'price' is the only neutral wait — reaching 'capture' or 'review' at all
  // means currentStage found something outstanding there (see its own logic).
  const needsAction = stage === 'capture' || stage === 'review';

  return (
    <div className="stagecell">
      <div className="stagetrack">
        {STAGE_ORDER.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <div className={`seg ${i <= at ? 'done' : ''}`} />}
            <div className={`node ${i < at ? 'done' : i === at ? 'current' : ''}`} />
          </React.Fragment>
        ))}
      </div>
      <div className="stagelabels">
        {STAGE_ORDER.map((s, i) => (
          <span key={s} className={i < at ? 'done' : i === at ? 'current' : ''}>
            {STAGE_LABEL[s]}
          </span>
        ))}
      </div>
      <div className={`stagewhat${needsAction ? ' attention' : ''}`}>
        {stage === 'capture' && shotsLeft > 0 && (
          <><b className="count">{job.shotsTaken} / {shotsTotal}</b> shots taken
            <span className="sub">{shotsLeft} angle{shotsLeft === 1 ? '' : 's'} still missing</span></>
        )}
        {stage === 'capture' && shotsLeft === 0 && (
          <><b className="count">{shotsTotal} / {shotsTotal}</b> shots taken
            <span className="sub">ready to read</span></>
        )}
        {stage === 'review' && (
          <><b className="count">{job.proposalsDecided} / {job.proposalsTotal}</b> proposals decided
            <span className="sub">waiting on your verdict</span></>
        )}
        {stage === 'price' && (
          <>Reviewed <span className="sub">ready for you to finish pricing</span></>
        )}
      </div>
    </div>
  );
}

function useSearch<T>(rows: T[], fields: (row: T) => string[]) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => fields(r).some((f) => f.toLowerCase().includes(needle)));
  }, [rows, q, fields]);
  return { q, setQ, filtered };
}

const STATUS_CLASS: Record<JobSummary['status'], string> = {
  open: 'plain', issued: 'lock', awaiting_approval: 'need',
  invoiced: 'auto', printed: 'auto', total_loss: 'bad', declined: 'bad',
};

export function Jobs({ rows, onOpen, onNew, onEdit }: {
  rows: JobSummary[];
  onOpen: (reference: string) => void;
  onNew: () => void;
  /** Absent in the preview build, which has nothing to write to. */
  onEdit?: (reference: string, patch: { vin?: string | null; customerEmail?: string }
  ) => Promise<void>;
}) {
  const { t, money } = useSession();
  const { q, setQ, filtered } = useSearch(rows,
    (j) => [j.reference, j.customer, j.customerEmail ?? '', j.vehicle, j.vin ?? '', j.impact]);
  // Email, VIN and impact point moved off the row itself to make room for the
  // stage track — they didn't disappear, they're one click away.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (ref: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(ref)) next.delete(ref); else next.add(ref);
    return next;
  });

  // The two fields most often wrong at intake, editable from the row that
  // already shows them — not a reason to open the job or go find the
  // customer. Impact point stays read-only: it drives the whole shot list,
  // and changing it after capture has started would orphan what was taken.
  const [editing, setEditing] = useState<string | null>(null);
  const [rowDraft, setRowDraft] = useState({ email: '', vin: '' });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const startEdit = (j: JobSummary) => {
    setOpen((prev) => new Set(prev).add(j.reference));
    setEditing(j.reference);
    setRowDraft({ email: j.customerEmail ?? '', vin: j.vin ?? '' });
    setProblem(null);
  };

  const saveEdit = useCallback(async (reference: string) => {
    if (!onEdit) return;
    setBusy(true);
    setProblem(null);
    try {
      await onEdit(reference, { vin: rowDraft.vin.trim() || null, customerEmail: rowDraft.email });
      setEditing(null);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [rowDraft, onEdit]);

  return (
    <section className="screen">
      <h2>{t('Jobs')}</h2>
      <p className="muted lede">
        Most recent first. Search by job number, customer, vehicle or plate.
      </p>

      <div className="searchrow">
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search jobs, customers, emails, vehicles, VINs…"
               aria-label="Search jobs" />
        <button className="btn primary" onClick={onNew}>+ New job</button>
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Job</th><th>Customer</th><th>Vehicle</th><th>Stage</th>
              <th className="num">Total</th><th />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="empty">Nothing matches “{q}”.</td></tr>
            ) : filtered.map((j) => (
              <React.Fragment key={j.reference}>
                <tr className="click" onClick={() => onOpen(j.reference)}>
                  <td className="mono">{j.reference}</td>
                  <td>
                    {j.customer}{' '}
                    {j.needsApproval && <span className="flag need">APPR</span>}
                  </td>
                  <td>{j.vehicle}</td>
                  <td>
                    {j.status === 'open'
                      ? <StageTrack job={j} />
                      : <span className={`flag ${STATUS_CLASS[j.status]}`}>
                          {t(STATUS_LABEL[j.status])}
                        </span>}
                  </td>
                  <td className="num">{j.total === null ? '—' : money(j.total)}</td>
                  <td className="detailbtncell">
                    {onEdit && (
                      <button className="detailbtn" onClick={(e) => { e.stopPropagation(); startEdit(j); }}>
                        Edit
                      </button>
                    )}
                    <button className="detailbtn" aria-expanded={open.has(j.reference)}
                            aria-controls={`det-${j.reference}`}
                            onClick={(e) => { e.stopPropagation(); toggle(j.reference); }}>
                      <span className="chev">▸</span> Details
                    </button>
                  </td>
                </tr>
                {open.has(j.reference) && (
                  <tr className="detailsrow open" id={`det-${j.reference}`}>
                    <td colSpan={6}>
                      {editing === j.reference ? (
                        <div className="detailedit" onClick={(e) => e.stopPropagation()}>
                          <label className="field">
                            <span>Email</span>
                            <input type="email" value={rowDraft.email} required
                                   onChange={(e) => setRowDraft({ ...rowDraft, email: e.target.value })} />
                          </label>
                          <label className="field">
                            <span>VIN</span>
                            <input className="mono" value={rowDraft.vin} maxLength={25}
                                   onChange={(e) => setRowDraft({ ...rowDraft, vin: e.target.value })} />
                          </label>
                          <span className="dk">Impact point</span>
                          <span className="dv">{j.impact || '—'}</span>
                          <button className="btn small primary" disabled={busy}
                                  onClick={() => void saveEdit(j.reference)}>
                            {busy ? '…' : 'Save'}
                          </button>
                          <button className="btn small" onClick={() => setEditing(null)}>Cancel</button>
                          {problem && <span className="err">{problem}</span>}
                        </div>
                      ) : (
                        <div className="detailgrid">
                          <div><span className="dk">Email</span>
                            <span className="dv">{j.customerEmail ?? '—'}</span></div>
                          <div><span className="dk">VIN</span>
                            <span className="dv mono">{j.vin ?? '—'}</span></div>
                          <div><span className="dk">Impact point</span>
                            <span className="dv">{j.impact || '—'}</span></div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface NewCustomer {
  name: string;
  kind: 'individual' | 'fleet' | 'dealer';
  needsApproval: boolean;
  email: string;
  phone: string;
}

export function Customers({ rows, onToggleApproval, onCreate, onUpdate }: {
  rows: CustomerSummary[];
  onToggleApproval: (id: string, needsApproval: boolean) => void;
  /** Absent in the preview build, which has nothing to write to. */
  onCreate?: (input: NewCustomer) => Promise<void>;
  onUpdate?: (id: string, patch: {
    name?: string; email?: string | null; phone?: string | null; terms?: string;
  }) => Promise<void>;
}) {
  const { t } = useSession();
  // Email is searchable too: a shop looking for "who is bob@" should find them.
  const { q, setQ, filtered } = useSearch(rows,
    (c) => [c.name, c.kind, c.terms, c.email ?? '']);

  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<NewCustomer>({
    name: '', kind: 'individual', needsApproval: true, email: '', phone: '',
  });
  const [editEmail, setEditEmail] = useState<string | null>(null);
  const [emailVal, setEmailVal] = useState('');
  // The full record, for everything the email cell cannot reach.
  const [editRow, setEditRow] = useState<string | null>(null);
  const [rowDraft, setRowDraft] = useState({ name: '', email: '', phone: '', terms: '' });

  const saveRow = useCallback(async (id: string) => {
    if (!onUpdate) return;
    setBusy(true);
    setProblem(null);
    try {
      await onUpdate(id, {
        name: rowDraft.name.trim(),
        email: rowDraft.email.trim() || null,
        phone: rowDraft.phone.trim() || null,
        terms: rowDraft.terms.trim(),
      });
      setEditRow(null);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [rowDraft, onUpdate]);

  const saveEmail = useCallback(async (id: string) => {
    if (!onUpdate) return;
    setBusy(true);
    try {
      // Empty clears it rather than storing "", because an empty string is an
      // address that fails to send with a baffling error.
      await onUpdate(id, { email: emailVal.trim() || null });
      setEditEmail(null);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [emailVal, onUpdate]);

  return (
    <section className="screen">
      <h2>{t('Customers')}</h2>
      <p className="muted lede">
        The approval flag lives here, on the customer — not on the job. “This fleet
        always approves first” is a fact about the fleet.
      </p>

      <div className="searchrow">
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search customers…" aria-label="Search customers" />
        {onCreate && (
          <button className="btn primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ New customer'}
          </button>
        )}
      </div>

      {adding && onCreate && (
        <form className="newcust" onSubmit={(e) => {
          e.preventDefault();
          // Same minimal shape check as New Job's inline customer, and the
          // same reason: a customer with no email is one the approval step
          // will refuse to email later, once the estimate is already issued.
          if (!draft.name.trim() || !/^[^\s@]+@[^\s@]+$/.test(draft.email.trim()) || busy) return;
          setBusy(true);
          setProblem(null);
          onCreate({ ...draft, name: draft.name.trim(), email: draft.email.trim() })
            .then(() => {
              setAdding(false);
              setDraft({ name: '', kind: 'individual', needsApproval: true,
                         email: '', phone: '' });
            })
            .catch((err: unknown) => setProblem(
              err instanceof Error ? err.message : String(err)))
            .finally(() => setBusy(false));
        }}>
          <label className="field">
            <span>Name</span>
            <input value={draft.name} required autoFocus
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label className="field">
            <span>Email — where the approval goes</span>
            <input type="email" required value={draft.email} placeholder="name@example.com"
                   onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </label>
          <label className="field">
            <span>Phone</span>
            <input value={draft.phone}
                   onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
          </label>
          <label className="field">
            <span>Type</span>
            <select value={draft.kind}
                    onChange={(e) => setDraft({ ...draft,
                      kind: e.target.value as typeof draft.kind })}>
              <option value="individual">Individual</option>
              <option value="fleet">Fleet</option>
              <option value="dealer">Dealer</option>
            </select>
          </label>
          <label className="field">
            <span>Jobs end at</span>
            <select value={draft.needsApproval ? 'invoice' : 'printout'}
                    onChange={(e) => setDraft({ ...draft,
                      needsApproval: e.target.value === 'invoice' })}>
              <option value="invoice">Invoice — they must approve</option>
              <option value="printout">Printout — pays at the counter</option>
            </select>
          </label>
          <div className="newcustacts">
            <button className="btn primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Add customer'}
            </button>
            {problem && <span className="err">{problem}</span>}
          </div>
        </form>
      )}

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Customer</th><th>Email</th><th>Type</th><th>Terms</th>
              <th className="num">Vehicles</th><th>Jobs end at</th><th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <React.Fragment key={c.id}>
              <tr>
                <td>
                  <b>{c.name}</b>
                  <div className="mono small muted">SINCE {c.since.toUpperCase()}</div>
                </td>
                {/* The approval email goes here and nowhere else, so it is
                    edited where the customer lives rather than per job. */}
                <td className="emailcell">
                  {editEmail === c.id ? (
                    <span className="priceedit">
                      <input type="email" autoFocus value={emailVal}
                             placeholder="name@example.com"
                             aria-label={`Email for ${c.name}`}
                             onChange={(e) => setEmailVal(e.target.value)}
                             onKeyDown={(e) => {
                               if (e.key === 'Escape') setEditEmail(null);
                               if (e.key === 'Enter') void saveEmail(c.id);
                             }} />
                      <button className="btn small primary" disabled={busy}
                              onClick={() => void saveEmail(c.id)}>Save</button>
                      <button className="btn small"
                              onClick={() => setEditEmail(null)}>Cancel</button>
                    </span>
                  ) : onUpdate ? (
                    <button className="pricebtn"
                            title={c.email ? 'Change this email' : 'Add an email'}
                            onClick={() => { setEditEmail(c.id); setEmailVal(c.email ?? ''); }}>
                      {c.email || <span className="muted">— add</span>}
                    </button>
                  ) : (c.email || '—')}
                </td>
                <td>{c.kind}</td>
                <td>{c.terms}</td>
                <td className="num">{c.vehicles}</td>
                <td><b>{c.needsApproval ? 'Invoice' : 'Printout'}</b></td>
                <td className="custacts">
                  <button className="btn small"
                    onClick={() => onToggleApproval(c.id, !c.needsApproval)}>
                    {c.needsApproval ? t('Needs approval') : t('Auto-approve')}
                  </button>
                  {onUpdate && (
                    <button className="btn small" onClick={() => {
                      setEditRow(editRow === c.id ? null : c.id);
                      setRowDraft({ name: c.name, email: c.email ?? '',
                                    phone: c.phone ?? '', terms: c.terms ?? '' });
                    }}>
                      {editRow === c.id ? 'Close' : 'Edit'}
                    </button>
                  )}
                </td>
              </tr>
              {/* Expanded beneath the row rather than in a dialog: the shop is
                  usually correcting one field while looking at the rest. */}
              {editRow === c.id && (
                <tr className="editrow">
                  <td colSpan={7}>
                    <form className="newcust" onSubmit={(e) => {
                      e.preventDefault();
                      void saveRow(c.id);
                    }}>
                      <label className="field">
                        <span>Name</span>
                        <input value={rowDraft.name} required
                               onChange={(e) => setRowDraft({ ...rowDraft, name: e.target.value })} />
                      </label>
                      <label className="field">
                        <span>Email — where the approval goes</span>
                        <input type="email" value={rowDraft.email}
                               placeholder="name@example.com"
                               onChange={(e) => setRowDraft({ ...rowDraft, email: e.target.value })} />
                      </label>
                      <label className="field">
                        <span>Phone</span>
                        <input value={rowDraft.phone}
                               onChange={(e) => setRowDraft({ ...rowDraft, phone: e.target.value })} />
                      </label>
                      <label className="field">
                        <span>Terms</span>
                        <input value={rowDraft.terms} placeholder="Net 30, on collection…"
                               onChange={(e) => setRowDraft({ ...rowDraft, terms: e.target.value })} />
                      </label>
                      <div className="newcustacts">
                        <button className="btn primary" type="submit" disabled={busy}>
                          {busy ? 'Saving…' : 'Save changes'}
                        </button>
                        <button className="btn" type="button"
                                onClick={() => setEditRow(null)}>Cancel</button>
                        {problem && <span className="err">{problem}</span>}
                      </div>
                    </form>
                  </td>
                </tr>
              )}
            </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="note">
        <b>Changing this affects future jobs only</b>
        Anything already issued keeps the path it was issued under — the customer was
        told how that one would end.
      </div>
    </section>
  );
}

/** A blank row, so "add another" starts clean every time. */
const EMPTY_ROW = {
  partNumber: '', description: '', bodyStyle: '', make: '',
  partType: 'new_oem', price: '', replaceHours: '', repairHours: '', refinishHours: '',
};

export function Catalog({ rows, profile, onImport, onEdit, onAdd,
                         focus, onFocusHandled, onRemoveRow }: {
  rows: CatalogItem[];
  /** Absent in the preview build, where there is nothing to import into. */
  onImport?: (entries: CatalogEntry[]) => Promise<void>;
  /** Change one row's price. Same upsert the importer uses. */
  onEdit?: (entry: CatalogEntry) => Promise<void>;
  /** Add one row by hand, for the parts a shop meets one at a time. */
  onAdd?: (entry: CatalogEntry) => Promise<void>;
  /**
   * The shop's rates, used ONLY to show what the hours are worth as they type.
   *
   * The confusion this removes: the price column is the PART price, and hours
   * are worth whatever the Rates screen says an hour is worth. Nothing in the
   * catalog is per-hour. Showing the arithmetic live is quicker than any
   * amount of explaining it.
   */
  profile?: RateProfile;
  /** Arrive searching for this part, sent from a priced line. */
  focus?: string | null;
  onFocusHandled?: () => void;
  /** Delete a row outright, and used when an edit changes a row's identity. */
  onRemoveRow?: (key: { description: string; partNumber?: string;
                        bodyStyle?: string; make?: string }) => Promise<void>;
}) {
  const { t, money } = useSession();
  const askedOnce = rows.filter((r) => r.source === 'asked_once').length;
  const { q, setQ, filtered } = useSearch(rows,
    (r) => [r.partNumber ?? '', r.description, r.partType]);

  const fileRef = useRef<HTMLInputElement>(null);
  const [importPreview, setImportPreview] = useState<ParseResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [showFormat, setShowFormat] = useState(false);
  const [adding, setAdding] = useState(false);
  const [row, setRow] = useState({ ...EMPTY_ROW });
  const [addBusy, setAddBusy] = useState(false);
  const [addProblem, setAddProblem] = useState<string | null>(null);
  const [addedNote, setAddedNote] = useState<string | null>(null);
  /**
   * The row this form is editing, as it was BEFORE any change.
   *
   * Kept because a row's identity is its name + body style + make. Change any
   * of those and the old row has to be deleted, or the shop ends up with two
   * prices for one part and no way to tell which an estimate used.
   */
  const [editingOriginal, setEditingOriginal] = useState<CatalogItem | null>(null);

  const bodyRate = profile?.labourRates.body ?? 0;
  const refinishRate = profile?.labourRates.refinish ?? 0;
  const paintRate = profile?.paintMaterialsPerHour ?? 0;
  const n = (v: string) => (v.trim() ? Number(v) : 0);

  /** What this row would put on an estimate. Shown live as they type. */
  const preview = {
    part: n(row.price),
    replace: n(row.replaceHours) * bodyRate,
    repair: n(row.repairHours) * bodyRate,
    // Paint is BOTH: the refinish labour and the materials that go with it.
    // Splitting them out is the only way the paint figure stops being a
    // mystery — it is two numbers from two different rates.
    refinishLabour: n(row.refinishHours) * refinishRate,
    refinishMaterials: n(row.refinishHours) * paintRate,
  };

  /** Open the form on an existing row. */
  const startEdit = useCallback((r: CatalogItem) => {
    setEditingOriginal(r);
    setRow({
      partNumber: r.partNumber ?? '',
      description: r.description,
      bodyStyle: r.bodyStyle ?? '',
      make: r.make ?? '',
      partType: r.partType,
      price: String(r.price),
      replaceHours: r.replaceHours === undefined ? '' : String(r.replaceHours),
      repairHours: r.repairHours === undefined ? '' : String(r.repairHours),
      refinishHours: r.refinishHours === undefined ? '' : String(r.refinishHours),
    });
    setAdding(true);
    setAddedNote(null);
    setAddProblem(null);
  }, []);

  const submitRow = useCallback(async () => {
    if (!onAdd) return;
    const price = Number(row.price);
    if (!row.description.trim() || !Number.isFinite(price) || price < 0) {
      setAddProblem('A description and a price are needed.');
      return;
    }
    setAddBusy(true);
    setAddProblem(null);
    try {
      const hrs = (v: string) => (v.trim() ? Number(v) : undefined);
      await onAdd({
        description: row.description.trim(),
        partType: (row.partType || 'none') as CatalogEntry['partType'],
        price,
        ...(row.partNumber.trim() ? { partNumber: row.partNumber.trim() } : {}),
        ...(row.bodyStyle.trim()
          ? { bodyStyle: row.bodyStyle.trim() as never } : {}),
        ...(row.make.trim() ? { make: row.make.trim() } : {}),
        ...(hrs(row.replaceHours) !== undefined ? { replaceHours: hrs(row.replaceHours) } : {}),
        ...(hrs(row.repairHours) !== undefined ? { repairHours: hrs(row.repairHours) } : {}),
        ...(hrs(row.refinishHours) !== undefined
          ? { refinishHours: hrs(row.refinishHours) } : {}),
      });
      // If the row's IDENTITY changed, the old one has to go — an upsert
      // would leave it behind as a second price for the same part.
      const o = editingOriginal;
      const identityChanged = o && (
        o.description.trim().toLowerCase() !== row.description.trim().toLowerCase()
        || (o.bodyStyle ?? '') !== row.bodyStyle.trim()
        || (o.make ?? '') !== row.make.trim()
        || (o.partNumber ?? '') !== row.partNumber.trim());
      if (identityChanged && onRemoveRow) {
        await onRemoveRow({
          description: o.description,
          ...(o.partNumber ? { partNumber: o.partNumber } : {}),
          ...(o.bodyStyle ? { bodyStyle: o.bodyStyle } : {}),
          ...(o.make ? { make: o.make } : {}),
        });
      }

      setAddedNote(o ? `${row.description.trim()} updated.` : `${row.description.trim()} saved.`);
      setEditingOriginal(null);
      // Keep body style and make: a shop adding rows is usually working
      // through one vehicle type at a time.
      setRow({ ...EMPTY_ROW, bodyStyle: row.bodyStyle, make: row.make });
    } catch (err) {
      setAddProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setAddBusy(false);
    }
  }, [row, onAdd, editingOriginal, onRemoveRow]);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');

  /**
   * Arriving from a priced line: search for the part and mark the rows that
   * could have supplied it.
   *
   * The search box is filled rather than the row silently scrolled to, because
   * an estimator who followed a link needs to see WHY these rows and not
   * others — and there is usually more than one candidate.
   */
  useEffect(() => {
    if (!focus) return;
    setQ(focus);
    onFocusHandled?.();
  }, [focus, setQ, onFocusHandled]);
  const [cameFrom] = useState<string | null>(focus ?? null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  /** A price the shop can correct without re-importing the whole file. */
  const saveRow = useCallback(async (r: CatalogItem, key: string) => {
    const v = Number(editVal);
    if (!onEdit || !Number.isFinite(v) || v < 0) return;
    setBusyKey(key);
    try {
      await onEdit({
        description: r.description, partType: r.partType, price: v,
        ...(r.partNumber ? { partNumber: r.partNumber } : {}),
      });
      setEditKey(null);
    } finally {
      setBusyKey(null);
    }
  }, [editVal, onEdit]);

  /**
   * Read the file and SHOW what will happen, rather than importing on the spot.
   *
   * A price list is the shop's own money. Silently swallowing 400 rows — some
   * of them misread — is not something to do without letting somebody look
   * first. The preview is also where rejected rows get named, which is the
   * only way a shop can fix its file.
   */
  const chooseFile = useCallback(async (file: File) => {
    setDone(null);
    setImportPreview(parseCatalogCsv(await file.text()));
  }, []);

  const commit = useCallback(async () => {
    if (!importPreview || !onImport) return;
    setImporting(true);
    try {
      await onImport(importPreview.ok);
      setDone(`${importPreview.ok.length} ${importPreview.ok.length === 1 ? 'row' : 'rows'} imported.`);
      setImportPreview(null);
    } catch (err) {
      setDone(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  }, [importPreview, onImport]);

  /**
   * Download the catalog as CSV.
   *
   * A blob URL rather than a server round trip: the rows are already here, and
   * an export that needs the network is an export that fails in a workshop.
   */
  const exportCsv = useCallback(() => {
    const blob = new Blob([toCatalogCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `catalog-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <section className="screen">
      <h2>{t('Catalog')}</h2>
      <p className="muted lede">
        Your parts, your prices. Two ways in: a bulk upload of what you already know,
        and the app asking once when something is missing.
      </p>

      <div className="searchrow">
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search part number or description…" aria-label="Search catalog" />
        <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" hidden
               aria-label="Choose a CSV file"
               onChange={(e) => {
                 const f = e.target.files?.[0];
                 e.target.value = '';
                 if (f) void chooseFile(f);
               }} />
        {onImport && (
          <button className="btn" onClick={() => fileRef.current?.click()}>
            Import CSV
          </button>
        )}
        <button className="btn" onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </button>
        <button className="btn" onClick={() => setShowFormat((v) => !v)}>
          {showFormat ? 'Hide format' : 'What format?'}
        </button>
        {onAdd && (
          <button className="btn primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Close' : '+ Add a part'}
          </button>
        )}
      </div>

      {adding && onAdd && (
        <form className="partform" onSubmit={(e) => { e.preventDefault(); void submitRow(); }}>
          {editingOriginal && (
            <div className="editing-banner">
              <b>Editing “{editingOriginal.description}”</b>
              <span className="muted small">
                {[editingOriginal.make, editingOriginal.bodyStyle].filter(Boolean).join(' · ')
                  || 'any vehicle'}
              </span>
              <button type="button" className="btn small" onClick={() => {
                setEditingOriginal(null);
                setRow({ ...EMPTY_ROW });
              }}>Stop editing — add a new one instead</button>
            </div>
          )}
          <div className="pfgrid">
            <Combo label="Part name" value={row.description} required
                   onChange={(v) => setRow({ ...row, description: v })}
                   options={PART_NAMES} placeholder="front bumper cover"
                   hint="say it the way you would out loud" />
            <label className="field">
              <span>Part number <em className="hint">— optional</em></span>
              <input value={row.partNumber} placeholder="68321-A2"
                     onChange={(e) => setRow({ ...row, partNumber: e.target.value })} />
            </label>
            <Combo label="Body style" value={row.bodyStyle}
                   onChange={(v) => setRow({ ...row, bodyStyle: v })}
                   options={BODY_STYLES} placeholder="any vehicle"
                   hint="blank = every vehicle" />
            <Combo label="Make" value={row.make}
                   onChange={(v) => setRow({ ...row, make: v })}
                   options={MAKES} placeholder="any make"
                   hint="blank = every make" />
            <Combo label="Part type" value={row.partType}
                   onChange={(v) => setRow({ ...row, partType: v })}
                   options={PART_TYPE_OPTIONS} />
            <label className="field">
              <span>Part price <em className="hint">— for ONE part, not per hour</em></span>
              <input type="number" step="0.01" min="0" required value={row.price}
                     placeholder="412.00"
                     onChange={(e) => setRow({ ...row, price: e.target.value })} />
            </label>
          </div>

          <h4 className="pfhead">Your times for this panel</h4>
          <p className="muted small pfnote">
            Hours only. They are multiplied by <b>your rates</b> from the Rates screen —
            there is no per-hour figure to enter here. Leave any of them blank if you do
            not know it; <b>blank is not zero</b>.
          </p>
          <div className="pfgrid">
            <label className="field">
              <span>Replace hours <em className="hint">— fit a new one</em></span>
              <input type="number" step="0.1" min="0" value={row.replaceHours}
                     placeholder="2.0"
                     onChange={(e) => setRow({ ...row, replaceHours: e.target.value })} />
            </label>
            <label className="field">
              <span>Repair hours <em className="hint">— take the dent out</em></span>
              <input type="number" step="0.1" min="0" value={row.repairHours}
                     placeholder="2.5"
                     onChange={(e) => setRow({ ...row, repairHours: e.target.value })} />
            </label>
            <label className="field">
              <span>Refinish hours <em className="hint">— the paint</em></span>
              <input type="number" step="0.1" min="0" value={row.refinishHours}
                     placeholder="2.2"
                     onChange={(e) => setRow({ ...row, refinishHours: e.target.value })} />
            </label>
          </div>

          {profile && (
            <div className="pfpreview">
              <b>What this row puts on an estimate</b>
              <ul>
                <li><span>Part, if replaced</span>
                  <span className="mono">{money(preview.part)}</span></li>
                <li><span>Replace labour · {row.replaceHours || 0} hr × {money(bodyRate)}</span>
                  <span className="mono">{money(preview.replace)}</span></li>
                <li><span>Repair labour · {row.repairHours || 0} hr × {money(bodyRate)}</span>
                  <span className="mono">{money(preview.repair)}</span></li>
                <li className="paint"><span>Paint labour · {row.refinishHours || 0} hr × {money(refinishRate)}</span>
                  <span className="mono">{money(preview.refinishLabour)}</span></li>
                <li className="paint"><span>Paint materials · {row.refinishHours || 0} hr × {money(paintRate)}</span>
                  <span className="mono">{money(preview.refinishMaterials)}</span></li>
                <li className="sum"><span>Replace + paint</span>
                  <span className="mono">{money(preview.part + preview.replace
                    + preview.refinishLabour + preview.refinishMaterials)}</span></li>
                <li className="sum"><span>Repair + paint <em>(no part charged)</em></span>
                  <span className="mono">{money(preview.repair
                    + preview.refinishLabour + preview.refinishMaterials)}</span></li>
              </ul>
              <p className="muted small">Before tax. Shop materials are added per repair
                hour on the job itself.</p>
            </div>
          )}

          <div className="newcustacts">
            <button className="btn primary" type="submit" disabled={addBusy}>
              {addBusy ? 'Saving…' : editingOriginal ? 'Save changes' : 'Save part'}
            </button>
            <button className="btn" type="button"
                    onClick={() => { setRow({ ...EMPTY_ROW }); setAddedNote(null); }}>
              Clear
            </button>
            {addProblem && <span className="err">{addProblem}</span>}
            {addedNote && <span className="okmsg">✓ {addedNote} Add another.</span>}
          </div>
        </form>
      )}

      {showFormat && (
        <div className="note">
          <b>Four columns, in any order</b>
          <p className="muted small">
            <b>description</b> and <b>price</b> are required.{' '}
            <b>part_number</b> may be blank. <b>part_type</b> is one of
            {' '}<code>new_oem</code>, <code>aftermarket</code>, <code>used</code>,
            {' '}<code>existing</code>, <code>sublet</code>, <code>none</code> — and it
            understands the usual words too (OEM, A/M, LKQ, "labour only").
          </p>
          <pre className="csvsample">{SAMPLE_CSV}</pre>
          <p className="muted small">
            Headings are matched loosely, so <code>Part No.</code>, <code>SKU</code>,
            {' '}<code>Cost</code> and <code>Desc</code> all work. Prices may carry a
            currency symbol and thousands separators. <b>Nothing is imported until
            you have seen what it found.</b>
          </p>
        </div>
      )}

      {done && <div className="note good"><b>Import</b>{done}</div>}

      {importPreview && (
        <div className="importprev">
          <h3>
            {importPreview.ok.length} ready to import
            {importPreview.problems.length > 0 && `, ${importPreview.problems.length} cannot be read`}
          </h3>
          {importPreview.ignoredColumns.length > 0 && (
            <p className="muted small">
              Ignored {importPreview.ignoredColumns.length === 1 ? 'column' : 'columns'}:{' '}
              {importPreview.ignoredColumns.join(', ')} — nothing in the catalog holds those.
            </p>
          )}
          {/* Named, never dropped: a part that vanishes here becomes a part
              missing from an estimate, and nobody traces that back to a file. */}
          {importPreview.problems.length > 0 && (
            <ul className="importbad">
              {importPreview.problems.slice(0, 8).map((r) => (
                <li key={r.line}><b>Line {r.line}</b> — {r.problem}</li>
              ))}
              {importPreview.problems.length > 8 && (
                <li className="muted">…and {importPreview.problems.length - 8} more</li>
              )}
            </ul>
          )}
          {importPreview.ok.length > 0 && (
            <div className="tablewrap">
              <table>
                <thead><tr><th>Part no.</th><th>Description</th><th>Type</th>
                  <th className="num">Price</th></tr></thead>
                <tbody>
                  {importPreview.ok.slice(0, 6).map((e, i) => (
                    <tr key={i}><td className="mono">{e.partNumber || '—'}</td>
                      <td>{e.description}</td><td>{e.partType}</td>
                      <td className="num">{money(e.price)}</td></tr>
                  ))}
                </tbody>
              </table>
              {importPreview.ok.length > 6 && (
                <p className="muted small">…and {importPreview.ok.length - 6} more</p>
              )}
            </div>
          )}
          <div className="importacts">
            <button className="btn primary" disabled={importing || importPreview.ok.length === 0}
                    onClick={() => void commit()}>
              {importing ? 'Importing…' : `Import ${importPreview.ok.length}`}
            </button>
            <button className="btn" onClick={() => setImportPreview(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="tablewrap">
        <table>
          <thead>
            <tr><th>Part no.</th><th>Description</th><th>Fits</th><th>Type</th>
                <th className="num">Price</th>
                <th className="num">R&amp;R</th><th className="num">Repair</th>
                <th className="num">Paint</th><th>Source</th><th /></tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const key = `${r.partNumber ?? ''}|${r.description}`;
              return (
              <tr key={key}
                  className={cameFrom
                    && r.description.trim().toLowerCase()
                       === cameFrom.trim().toLowerCase() ? 'fromline' : undefined}>
                <td className="mono">{r.partNumber || '—'}</td>
                <td>{r.description}</td>
                {/* What this row applies to. "Any vehicle" is a real answer,
                    not a gap — it is the row that catches everything else. */}
                <td className="small muted">
                  {[r.bodyStyle, r.make].filter(Boolean).join(' · ') || 'any vehicle'}
                </td>
                <td>{r.partType}</td>
                <td className="num pricecell">
                  {editKey === key ? (
                    <span className="priceedit">
                      <input type="number" step="0.01" min="0" autoFocus value={editVal}
                             aria-label={`Price for ${r.description}`}
                             onChange={(e) => setEditVal(e.target.value)}
                             onKeyDown={(e) => {
                               if (e.key === 'Escape') setEditKey(null);
                               if (e.key === 'Enter') void saveRow(r, key);
                             }} />
                      <button className="btn small primary" disabled={busyKey === key}
                              onClick={() => void saveRow(r, key)}>
                        {busyKey === key ? '…' : 'Save'}
                      </button>
                      <button className="btn small" onClick={() => setEditKey(null)}>
                        Cancel
                      </button>
                    </span>
                  ) : onEdit ? (
                    <button className="pricebtn" title="Change this price"
                            onClick={() => { setEditKey(key); setEditVal(String(r.price)); }}>
                      {money(r.price)}
                    </button>
                  ) : money(r.price)}
                </td>
                {/* An em dash, never 0.0 — "nobody has said" must not read as
                    "this takes no time", which would be a free line. */}
                <td className="num mono small">{r.replaceHours ?? '—'}</td>
                <td className="num mono small">{r.repairHours ?? '—'}</td>
                <td className="num mono small">{r.refinishHours ?? '—'}</td>
                <td className={r.source === 'asked_once' ? 'teal' : 'muted'}>
                  {r.source === 'asked_once' ? 'Asked once · kept' : 'Bulk import'}
                </td>
                <td className="lineacts">
                  {onAdd && (
                    <button className="btn small" title="Open this row in the form"
                            onClick={() => startEdit(r)}>Edit</button>
                  )}
                  {onRemoveRow && (
                    <button className="btn small rm" title="Delete this row"
                            onClick={() => {
                              // Confirmed: a deleted price is not recoverable,
                              // and an estimate written afterwards will quietly
                              // start asking for it again.
                              const where = [r.make, r.bodyStyle].filter(Boolean).join(' · ')
                                || 'any vehicle';
                              if (!globalThis.confirm(
                                `Delete "${r.description}" (${where})?

`
                                + 'Estimates already issued keep the price they were '
                                + 'issued with. New ones will ask for it again.')) return;
                              void onRemoveRow({
                                description: r.description,
                                ...(r.partNumber ? { partNumber: r.partNumber } : {}),
                                ...(r.bodyStyle ? { bodyStyle: r.bodyStyle } : {}),
                                ...(r.make ? { make: r.make } : {}),
                              });
                            }}>Delete</button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="note">
        <b>It fills itself</b>
        {askedOnce} of these {rows.length} arrived because the app asked during
        an estimate and kept the answer. That prompt should fire less often every week —
        if it does not, the design is not working.
      </div>
    </section>
  );
}
